import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import Me from '../assets/Images/profile-img.png'
import { device, size } from '../utils/breakpoints'

const BoxWrapper = styled(motion.div)`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 65vw;
  height: 55vh;
  z-index: 5;
  
  /* Premium Glassmorphic Dual Split Modal */
  display: flex;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.08);

  @media ${device.tablet} {
    width: 85vw;
    height: 60vh;
    border-radius: 20px;
  }
  @media ${device.mobileL} {
    width: 90vw;
    height: auto;
    min-height: 75vh;
    flex-direction: column;
    overflow: visible;
  }
`

const LeftSubBox = styled.div`
  width: 50%;
  background: rgba(0, 0, 0, 0.82);
  backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  position: relative;
  border-right: 1px dashed rgba(252, 246, 244, 0.15);
  box-sizing: border-box;

  @media ${device.mobileL} {
    width: 100%;
    min-height: 38vh;
    border-right: none;
    border-bottom: 1px dashed rgba(252, 246, 244, 0.15);
    border-radius: 20px 20px 0 0;
  }
`

const RightSubBox = styled.div`
  width: 50%;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  box-sizing: border-box;

  @media ${device.mobileL} {
    width: 100%;
    min-height: 38vh;
    padding: 2.5rem 1.5rem;
    border-radius: 0 0 20px 20px;
  }
`

const Text = styled.div`
  font-size: calc(1em + 1.5vw);
  color: ${props => props.theme.body};
  padding: 3rem 2.5rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  gap: 0.5rem;
  min-width: 0;

  h1 {
    font-family: 'Karla', sans-serif;
    font-size: calc(1.8rem + 0.8vw);
    font-weight: 700;
    margin: 0;
    background: linear-gradient(to right, #FCF6F4, #d1d5db);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  h3 {
    font-family: 'Karla', sans-serif;
    font-size: calc(1.1rem + 0.3vw);
    font-weight: 600;
    color: rgba(252, 246, 244, 0.9);
    margin: 0;
  }

  @media ${device.tablet} {
    padding: 2rem 1.5rem;
    font-size: calc(0.9em + 1vw);
    h1 { font-size: 1.8rem; }
    h3 { font-size: 1.2rem; }
  }
  @media ${device.mobileL} {
    padding: 1.5rem 1.25rem;
    font-size: 1rem;
  }
`

const IntroDescription = styled(motion.h6)`
  color: rgba(252, 246, 244, 0.6);
  font-size: calc(0.55rem + 1.1vw);
  font-weight: 300;
  line-height: 1.6;
  width: 100%;
  margin-top: 1rem;
  font-family: 'Karla', sans-serif;
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  @media ${device.mobileL} {
    font-size: 0.85rem;
    line-height: 1.5;
    margin-top: 0.5rem;
  }
`

const AvatarFrame = styled(motion.div)`
  width: 270px;
  height: 270px;
  border-radius: 50%;
  border: 4px solid #000000;
  background: rgba(0, 0, 0, 0.03);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08), 0 0 0 8px rgba(255, 255, 255, 0.4);
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  position: relative;
  
  img {
    width: 90%;
    height: auto;
    object-fit: contain;
    object-position: bottom center;
    transition: transform 0.5s ease;
  }
  
  &:hover img {
    transform: scale(1.06);
  }

  @media ${device.tablet} {
    width: 190px;
    height: 190px;
  }
  @media ${device.mobileL} {
    width: 170px;
    height: 170px;
  }
`

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.4
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 100, damping: 15 }
  }
}

const imageVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { type: 'spring', stiffness: 80, damping: 14, delay: 0.3 }
  }
}

const Intro = () => {
  const [isMobile, setIsMobile] = useState(false)
  
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${size.mobileL})`)
    setIsMobile(mq.matches)
    const handler = () => setIsMobile(mq.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  return (
    <BoxWrapper
      initial={{ height: 0, opacity: 0 }}
      animate={{
        height: isMobile ? 'auto' : '55vh',
        opacity: 1
      }}
      transition={{
        height: { type: 'spring', stiffness: 50, damping: 15, delay: 0.5 },
        opacity: { duration: 0.6, delay: 0.3 }
      }}
    >
      <LeftSubBox>
        <Text
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.h1 variants={itemVariants}>Hi,</motion.h1>
          <motion.h3 variants={itemVariants}>I'm Gourav.</motion.h3>
          <IntroDescription className="intro-description" variants={itemVariants}>
            I craft elegant, user-focused web experiences with clean code and thoughtful design.
          </IntroDescription>
        </Text>
      </LeftSubBox>
      <RightSubBox>
        <motion.div
          className="pic-wrapper"
          variants={imageVariants}
          initial="hidden"
          animate="visible"
        >
          <AvatarFrame
            animate={{
              y: [0, -8, 0]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <img src={Me} alt="Gourav" />
          </AvatarFrame>
        </motion.div>
      </RightSubBox>
    </BoxWrapper>
  )
}

export default Intro
